import { useView } from 'aurelia-framework';
import * as moment from "moment";
import { I18N } from "aurelia-i18n";
import { Guid } from "../../../../utilities/guid";
import { Container, autoinject, transient } from 'aurelia-dependency-injection';
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { BindingEngine, computedFrom, observable } from "aurelia-binding";
import { DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import { Reflection } from "../../../../utilities/reflection";
import { EmailStatusesEnum } from "../../../../models/basics/basics.models";
import { DataGrid } from "../../../components/data-grid/data-grid";
import { EmailService } from "../../../../services/basics/email-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { BaseClaims } from '@rightship-tech/common-ui/constants/authentication/base-claims';
import { UserClaimsService } from '../../../../services/authentication/user-claims.service';

@autoinject()
@transient()
@useView('views/pages/modules/email-history/email-history.html')
export class EmailHistory extends CustomGridPageBase {

    @observable
    filter: string;

    public appliedFilter: string;
    public initialFilters: QueryFilter[];
    public isColumnFiltering: boolean = false;
    private odataUrl = "odata/EmailHistories";
    public selectedFilter: { title: string, clear: () => void, apply: () => void };
    public selectedStatusFilter: number[] = [];
    public canResendEmail: boolean;
    public isMobileFilterOpen: boolean = false;
    public mobileSelectedfilters: any[] = []

    constructor(i18N: I18N, protected bindingEngine: BindingEngine,
        private notificationClientService: NotificationClientService,
        private userClaimsService: UserClaimsService) {
        super(i18N, "Email History", bindingEngine);
        this.initialSorting = this.getSorting();
        this.canResendEmail = this.userClaimsService.hasPermission(BaseClaims.ResendEmail);
    }

    public getSorting() {
        let columns: DataGridColumn[] = [];
        columns = []

        return columns;
    }

    activate(params) {
        this.columns = this.getColumns();

        if (params.emailHistoryId) {
            this.idIsApplied = true;

            this.idFirstFilter = params.emailHistoryId;
            this.idSecondFilter = params.emailHistoryId;
        }

        this.setItemSource();
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                name: "date",
                title: "Date",
                isSortable: true,
                isAscending: false,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : '',
            },
            {
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.Default,
                name: 'recipientEmail',
                title: "Email",
                isSortable: true
            },
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'status',
                title: "Status",
                isSortable: true
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'recipientEntityType',
                title: "Entity",
                isSortable: true
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                name: 'statusNotes',
                title: "Status Notes",
                isSortable: false
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Number,
                name: 'recipientEntityId',
                title: "Recipient Entity Id",
                isSortable: true
            },
            {
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.Default,
                name: 'notes',
                title: "Notes",
                isSortable: false
            },
            {
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.Number,
                name: 'tries',
                title: "Tries",
                isSortable: true
            },
            {
                name: 'Resend',
                title: "",
                dataType: DataType.Button,
                isHidden: (item) => {
                    return item.statusId != EmailStatusesEnum.Failed || !this.canResendEmail;
                },
                parameter: "Resend",
                columnOrder: 100,
                sortOrder: 100,
                width: "125px",
                value: (item, row, colum, dataGridVM: DataGrid) => {
                    return new Promise<void>((resolve, reject) => {
                        const emailService = Container.instance.get(EmailService) as EmailService;
                        emailService.resendEmail(item.id).then(response => {
                            if (response) {
                                dataGridVM.reloadData();
                                resolve();
                            }
                            else {
                                reject();
                                this.notificationClientService.show("Email cannot be sent!", "", NotificationType.Error);
                            }
                        }).catch(() => {
                            reject();
                            this.notificationClientService.show("Email cannot be sent!", "", NotificationType.Error);
                        });
                    })
                },
            }
        ];
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: this.initialFilters,
            initialSorting: this.initialSorting
        };
    }

    closeAllFilters() {
        this.showIdFilter = false;
        this.showDateFilter = false;
        this.showStatusFilter = false;
        this.showAttachmentFilter = false;
        this.showCcFilter = false;
        this.showBccFilter = false;
        this.showEntityFilter = false;
        this.showTriesFilter = false;
    }

    private setItemSource() {
        const currentFilters: QueryFilter[] = [];

        //Applied filters
        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("recipientEmail", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("statusNotes", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("notes", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, true, guid, true, true));
        }

        // id filter
        if (this.idIsApplied) {
            const guid = Guid.generateGuid()
            currentFilters.push(QueryFilter.getQuery("emailHistoryId ", this.idFirstFilter, "ge", DataType.Number, true, false, guid, true, false))
            currentFilters.push(QueryFilter.getQuery("emailHistoryId ", this.idSecondFilter, "le", DataType.Number, true, false, guid, true, false))
        }

        // date filter
        if (this.dateFilterActive) {
            const guid = Guid.generateGuid()
            if (moment(this.endDate).isSame(moment(this.startDate))) {
                const newD = moment(this.endDate).add(1, "day").subtract(1, "minute")
                this.endDate = newD.toDate();
            }
            currentFilters.push(QueryFilter.getQuery('date', this.startDate, 'ge', DataType.Date, true, false, guid, true));
            currentFilters.push(QueryFilter.getQuery('date', this.endDate, 'le', DataType.Date, true, false, guid, true));
        }

        // status filter
        if (this.statusFilterActive) {
            const statusIdFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "statusId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: "EmailStatuses",
                    isNullable: true,
                },
                filterValue: this.selectedStatuses || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(statusIdFilter)
        }

        // attachment filter
        if (this.attachmentFilterActive) {
            if (this.selectedAttachments.length == 1) {
                const hasAttachments = this.selectedAttachments[0].id == 0;
                currentFilters.push(QueryFilter.getQuery('hasAttachments', hasAttachments, "eq", DataType.Boolean, true))
            }
        }

        // cc filter
        if (this.ccFilterActive) {
            if (this.selectedCcs.length == 1) {
                const includeCcs = this.selectedCcs[0].id == 0;
                currentFilters.push(QueryFilter.getQuery('isCc', includeCcs, "eq", DataType.Boolean, true))
            }
        }

        // bcc filter
        if (this.bccFilterActive) {
            if (this.selectedBccs.length == 1) {
                const includeBccs = this.selectedBccs[0].id == 0;
                currentFilters.push(QueryFilter.getQuery('isBcc ', includeBccs, "eq", DataType.Boolean, true))
            }
        }

        // entity filter
        if (this.entityFilterActive) {
            const entityIdFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "recipientEntityTypeId ",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: "EntityTypes",
                    isNullable: true,
                },
                filterValue: this.selectedEntities || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(entityIdFilter)
        }

        // tries filter
        if (this.triesFilterActive) {
            const guid = Guid.generateGuid()
            currentFilters.push(QueryFilter.getQuery("tries ", this.triesFirstFilter, "ge", DataType.Number, true, false, guid, true, false))
            currentFilters.push(QueryFilter.getQuery("tries ", this.triesSecondFilter, "le", DataType.Number, true, false, guid, true, false))
        }

        this.itemSource = this.getItemSource(currentFilters);
    }

    //#region Id filter
    public idDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public idFirstFilter: number = this.idDefaults.low;
    public idSecondFilter: number = this.idDefaults.high;
    public showIdFilter: boolean;
    public idIsApplied: boolean;

    toggleIdFilter() {
        if (!this.showIdFilter) {
            this.closeAllFilters();
        }
        this.showIdFilter = !this.showIdFilter;
    }

    public idSetSelectedFilter() {
        this.selectedFilter = {
            title: "Id",
            clear: () => {
                this.clearIdFilter();
            },
            apply: () => {
                this.showIdFilter = false;
            }
        }
    }

    public clearIdFilter(setSource = true) {
        this.idFirstFilter = this.idDefaults.low;
        this.idSecondFilter = this.idDefaults.high;
        this.idIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyIdFilter(event) {
        this.idIsApplied = event.detail.closeFilter;
        this.setItemSource();
        this.showIdFilter = false;
    }

    //#endregion

    // #region Date filter
    public startDate: Date
    public endDate: Date
    public showDateFilter: boolean;
    public dateIsApplied: boolean;

    toggleDateFilter() {
        if (!this.showDateFilter) {
            this.closeAllFilters();
        }
        this.showDateFilter = !this.showDateFilter;
    }

    public dateSetSelectedFilter() {
        this.selectedFilter = {
            title: "Date",
            clear: () => {
                this.clearDateFilter();
            },
            apply: () => {
                this.showDateFilter = false;
            }
        }
    }

    public clearDateFilter(setSource = true) {
        this.startDate = null
        this.endDate = null
        this.dateIsApplied = false;
        this.showDateFilter = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyDateFilter(event) {

        //for clearing the filter
        if (!event.closeFilter) {
            this.clearDateFilter();
            return;
        }

        this.setItemSource();
        this.showDateFilter = false;
    }

    @computedFrom("startDate", "endDate")
    public get dateFilterActive() {
        return (this.startDate && this.endDate);
    }
    //#endregion

    // #region Status filter
    public selectedStatuses: [] = []
    public showStatusFilter: boolean;
    public statusIsApplied: boolean;

    toggleStatusFilter() {
        if (!this.showStatusFilter) {
            this.closeAllFilters();
        }
        this.showStatusFilter = !this.showStatusFilter;
    }

    public statusSetSelectedFilter() {
        this.selectedFilter = {
            title: "Status",
            clear: () => {
                this.clearStatusFilter();
            },
            apply: () => {
                this.showStatusFilter = false;
            }
        }
    }

    public clearStatusFilter(setSource = true) {
        this.selectedStatuses = []
        this.statusIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyStatusFilter() {

        this.setItemSource();
        this.showStatusFilter = false;

    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.selectedStatuses),
        Reflection.getPropertyPath<EmailHistory>(z => z.selectedStatuses.length))
    public get statusFilterActive() {
        return this.selectedStatuses && this.selectedStatuses.length > 0
    }
    //#endregion

    // #region Attachment filter
    public selectedAttachments: any[] = []
    public showAttachmentFilter: boolean;
    public attachmentIsApplied: boolean;
    public yesNoOptionsAttachment: any[] = [{ id: 0, name: "Yes" }, { id: 1, name: "No" }]

    toggleAttachmentFilter() {
        if (!this.showAttachmentFilter) {
            this.closeAllFilters();
        }
        this.showAttachmentFilter = !this.showAttachmentFilter;
    }

    public attachmentSetSelectedFilter() {
        this.selectedFilter = {
            title: "Attachment",
            clear: () => {
                this.clearAttachmentFilter();
            },
            apply: () => {
                this.showAttachmentFilter = false;
            }
        }
    }

    public clearAttachmentFilter(setSource = true) {
        this.selectedAttachments = []
        this.attachmentIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyAttachmentFilter(items: any[] = null) {
        this.selectedAttachments = items['items'].filter(x => x.isSelected);
        this.setItemSource();
        this.showAttachmentFilter = false;
    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.selectedAttachments),
        Reflection.getPropertyPath<EmailHistory>(z => z.selectedAttachments.length))
    public get attachmentFilterActive() {
        return this.selectedAttachments && this.selectedAttachments.length > 0
    }
    //#endregion

    // #region Cc filter
    public selectedCcs: any[] = []
    public showCcFilter: boolean;
    public ccIsApplied: boolean;
    public yesNoOptionsCc: any[] = [{ id: 0, name: "Yes" }, { id: 1, name: "No" }]

    toggleCcFilter() {
        if (!this.showCcFilter) {
            this.closeAllFilters();
        }
        this.showCcFilter = !this.showCcFilter;
    }

    public ccSetSelectedFilter() {
        this.selectedFilter = {
            title: "CC",
            clear: () => {
                this.clearCcFilter();
            },
            apply: () => {
                this.showCcFilter = false;
            }
        }
    }

    public clearCcFilter(setSource = true) {
        this.selectedCcs = []
        this.ccIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyCcFilter(items: any[] = null) {
        this.selectedCcs = items['items'].filter(x => x.isSelected);
        this.setItemSource();
        this.showCcFilter = false;
    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.selectedCcs),
        Reflection.getPropertyPath<EmailHistory>(z => z.selectedCcs.length))
    public get ccFilterActive() {
        return this.selectedCcs && this.selectedCcs.length > 0
    }
    //#endregion

    // #region Bcc filter
    public selectedBccs: any[] = []
    public showBccFilter: boolean;
    public bccIsApplied: boolean;
    public yesNoOptionsBcc: any[] = [{ id: 0, name: "Yes" }, { id: 1, name: "No" }]

    toggleBccFilter() {
        if (!this.showBccFilter) {
            this.closeAllFilters();
        }
        this.showBccFilter = !this.showBccFilter;
    }

    public bccSetSelectedFilter() {
        this.selectedFilter = {
            title: "BCC",
            clear: () => {
                this.clearBccFilter();
            },
            apply: () => {
                this.showBccFilter = false;
            }
        }
    }

    public clearBccFilter(setSource = true) {
        this.selectedBccs = []
        this.bccIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyBccFilter(items: any[] = null) {
        this.selectedBccs = items['items'].filter(x => x.isSelected);
        this.setItemSource();
        this.showBccFilter = false;
    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.selectedBccs),
        Reflection.getPropertyPath<EmailHistory>(z => z.selectedBccs.length))
    public get bccFilterActive() {
        return this.selectedBccs && this.selectedBccs.length > 0
    }
    //#endregion

    // #region Entity filter
    public selectedEntities: [] = []
    public showEntityFilter: boolean;
    public entityIsApplied: boolean;

    toggleEntityFilter() {
        if (!this.showEntityFilter) {
            this.closeAllFilters();
        }
        this.showEntityFilter = !this.showEntityFilter;
    }

    public entitySetSelectedFilter() {
        this.selectedFilter = {
            title: "Entity",
            clear: () => {
                this.clearEntityFilter();
            },
            apply: () => {
                this.showEntityFilter = false;
            }
        }
    }

    public clearEntityFilter(setSource = true) {
        this.selectedEntities = []
        this.entityIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyEntityFilter() {

        this.setItemSource();
        this.showEntityFilter = false;

    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.selectedEntities),
        Reflection.getPropertyPath<EmailHistory>(z => z.selectedEntities.length))
    public get entityFilterActive() {
        return this.selectedEntities && this.selectedEntities.length > 0
    }
    //#endregion

    //#region Tries filter
    public triesDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public triesFirstFilter: number = this.triesDefaults.low;
    public triesSecondFilter: number = this.triesDefaults.high;
    public showTriesFilter: boolean;
    public triesIsApplied: boolean;

    toggleTriesFilter() {
        if (!this.showTriesFilter) {
            this.closeAllFilters();
        }
        this.showTriesFilter = !this.showTriesFilter;
    }

    public triesSetSelectedFilter() {
        this.selectedFilter = {
            title: "Tries",
            clear: () => {
                this.clearTriesFilter();
            },
            apply: () => {
                this.showTriesFilter = false;
            }
        }
    }

    public clearTriesFilter(setSource = true) {
        this.triesFirstFilter = this.triesDefaults.low;
        this.triesSecondFilter = this.triesDefaults.high;
        this.triesIsApplied = false;

        if (setSource) {
            this.setItemSource();
        }
    }

    public applyTriesFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.triesFirstFilter = filterValue.low;
            this.triesSecondFilter = filterValue.high;
            this.triesIsApplied = true;
        }

        this.setItemSource();
        this.showTriesFilter = false;
    }


    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.triesFirstFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.triesSecondFilter))
    public get triesFilterActive(): boolean {
        return this.triesFirstFilter > this.triesDefaults.low || this.triesSecondFilter < this.triesDefaults.high;
    }
    //#endregion

    //#region column sorting 
    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map((bId: number) => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
        this.isColumnFiltering = this.dataGridVM.visibleColumns.some(item => item.isAscending === true || item.isAscending === false);
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.showColumnSorting),
        Reflection.getPropertyPath<EmailHistory>(z => z.showIdFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showDateFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showStatusFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showAttachmentFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showCcFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showBccFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showEntityFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showTriesFilter))
    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting || this.showIdFilter || this.showDateFilter || this.showStatusFilter || this.showAttachmentFilter || this.showCcFilter || this.showBccFilter ||
            this.showEntityFilter || this.showTriesFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showIdFilter = false;
        this.showColumnSorting = false;
        this.showIdFilter = false;
        this.showDateFilter = false;
        this.showStatusFilter = false;
        this.showAttachmentFilter = false;
        this.showCcFilter = false;
        this.showBccFilter = false;
        this.showEntityFilter = false;
        this.showTriesFilter = false;
    }

    public mobileApplyAllFilters(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.selectedStatusFilter = [];

        this.mobileSelectedfilters.forEach(x => {
            x.apply();
        })

        //Refresh itemSource irrelevant of number of mobileSelectedFilters. Users may unselect all filters (resulting in no selected filters)
        this.setItemSource();
        this.mobileSelectedfilters = []
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public mobileAddSelectedFilter() {
        if (this.selectedFilter) {
            this.mobileSelectedfilters.push(this.selectedFilter)
            this.selectedFilter = null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<EmailHistory>(z => z.showColumnSorting),
        Reflection.getPropertyPath<EmailHistory>(z => z.showIdFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showDateFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showStatusFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showAttachmentFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showCcFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showBccFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showEntityFilter),
        Reflection.getPropertyPath<EmailHistory>(z => z.showTriesFilter))
    public get mobileFilterSelected() {
        return this.showColumnSorting || this.showDateFilter || this.showStatusFilter || this.showAttachmentFilter || this.showCcFilter || this.showBccFilter ||
            this.showEntityFilter || this.showTriesFilter
    }

    public clearAllFilters() {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
            this.isColumnFiltering = false
        }
        this.appliedFilter = null;
        this.clearIdFilter(false);
        this.clearDateFilter(false);
        this.clearStatusFilter(false);
        this.clearAttachmentFilter(false);
        this.clearCcFilter(false);
        this.clearBccFilter(false);
        this.clearEntityFilter(false);
        this.clearTriesFilter(false);

        this.setItemSource();
    }

    filterChanged() {
        if ((this.filter) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }
}